import type { CSSProperties } from 'react';

import { areaStyle, resolveColor } from '@/features/storefront/appearance';

import type {
  PagePart,
  PartAlignment,
  PartBackground,
  PartSpace,
  PartTextCase,
  PartTextSize,
  PartTextWeight,
  PartType,
} from '@/features/storefront/sections/parts';
import type { SectionDevice } from '@/features/storefront/sections/primitives';
import { cn } from '@/lib/cn';

/**
 * A part's presentation, turned into classes and a style object.
 *
 * Every control here has a `theme` value that means "leave it alone", and
 * leaving it alone is the default on every part of every shop. So the job of
 * this module is mostly to return nothing: a merchant who has moved their buy
 * button but not restyled it must get the button their theme drew, down to the
 * margin above it.
 *
 * "Nothing" is load-bearing on spacing in particular. The parts used to be
 * lines of markup inside `BuyBox` with `mt-1`, `mt-4` and `mt-8` written on
 * them, and those margins stay written on them: a part keeps its own spacing
 * until the merchant chooses some, and {@link partOwnsSpacing} is what each
 * part component asks before applying its. A scale of six steps could not
 * reproduce a page built out of nine different margins, and a product page
 * that shifted by four pixels on every shop in the world the day this shipped
 * would be a worse bug than any this feature fixes.
 *
 * Classes rather than inline styles wherever Tailwind has a token for it, so a
 * part keeps the shop's own type scale and responds to the breakpoints like
 * everything around it. Colours are inline, because they are CSS variables the
 * theme sets on an ancestor and Tailwind cannot name.
 */

const SPACE_ABOVE: Record<Exclude<PartSpace, 'theme'>, string> = {
  none: 'mt-0',
  xs: 'mt-2',
  sm: 'mt-4',
  md: 'mt-6',
  lg: 'mt-10',
  xl: 'mt-16',
};

const SPACE_BELOW: Record<Exclude<PartSpace, 'theme'>, string> = {
  none: 'mb-0',
  xs: 'mb-2',
  sm: 'mb-4',
  md: 'mb-6',
  lg: 'mb-10',
  xl: 'mb-16',
};

const TEXT_SIZE: Record<Exclude<PartTextSize, 'theme'>, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
  '4xl': 'text-4xl',
  '5xl': 'text-5xl',
};

const TEXT_WEIGHT: Record<Exclude<PartTextWeight, 'theme'>, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

const TEXT_CASE: Record<Exclude<PartTextCase, 'theme'>, string> = {
  none: 'normal-case',
  uppercase: 'uppercase tracking-wide',
  capitalize: 'capitalize',
};

const ALIGN: Record<Exclude<PartAlignment, 'theme'>, string> = {
  left: 'text-start',
  center: 'text-center',
  right: 'text-end',
};

const BACKGROUND: Record<Exclude<PartBackground, 'theme' | 'none'>, string> = {
  surface: 'var(--storefront-surface)',
  primary: 'var(--storefront-primary)',
};

/**
 * The shop's own colour roles. The palette's own names first, so "Text" on a
 * part inside a section the merchant recoloured still means the theme's text
 * colour - the picker shows the palette's swatch, and must paint it.
 */
const COLOR_ROLES: Record<string, string> = {
  ink: 'var(--storefront-palette-text, var(--storefront-text))',
  muted: 'var(--storefront-palette-muted, var(--storefront-muted))',
  primary: 'var(--storefront-palette-primary, var(--storefront-primary))',
  background: 'var(--storefront-palette-background, var(--storefront-background))',
  surface: 'var(--storefront-palette-surface, var(--storefront-surface))',
};

const HIDDEN_ON: Record<SectionDevice, string> = {
  mobile: 'max-sm:hidden',
  tablet: 'max-lg:sm:hidden',
  desktop: 'lg:hidden',
};

/**
 * The type each component has always been set in, where the shop sets it rather
 * than the theme.
 *
 * The same job {@link PartPresentation.classes} does for a theme, for the
 * headings the storefront sized itself: a catalog's `text-3xl sm:text-4xl`, a
 * cart's `text-3xl`, a till's `text-4xl`. Written here rather than left on the
 * markup, because the markup is a slot now and a size on both the slot and the
 * wrapper is two font sizes on one element - decided by whichever Tailwind
 * emitted last, which is not a thing a merchant can predict.
 *
 * A theme's own string beats this, and the merchant's choice beats both.
 */
const BASE_CLASSES: Partial<Record<PartType, string>> = {
  listing_heading: 'text-3xl tracking-tight sm:text-4xl',
  category_heading: 'text-3xl tracking-tight sm:text-4xl',
  search_heading: 'text-3xl tracking-tight sm:text-4xl',
  search_count: 'text-sm',
  cart_heading: 'text-3xl',
  cart_summary_heading: 'text-lg font-semibold',
  checkout_heading: 'text-4xl',
  checkout_summary_heading: 'text-xs font-semibold uppercase tracking-[0.22em]',
  product_title: 'text-3xl',
  product_brand: 'text-sm uppercase tracking-wide',
  product_price: 'text-2xl font-semibold',
  product_compare_price: 'text-lg',
  product_tax_note: 'text-xs',
  product_sku: 'text-xs',
  product_availability: 'text-sm',
  product_short_description: 'text-base',
  custom_heading: 'text-2xl font-semibold',
  custom_text: 'text-sm',
};

/**
 * The colour each component has always been drawn in, where the shop draws it
 * in something other than body text.
 *
 * Named as one of the shop's own roles rather than as a hex, so a merchant who
 * repaints their palette carries every one of these with them. Applied only
 * while the merchant has not picked a colour, exactly like {@link BASE_CLASSES}
 * - and, like it, here rather than on the markup, because a colour on the
 * element itself would beat the wrapper and the picker would do nothing.
 */
const BASE_COLORS: Partial<Record<PartType, string>> = {
  product_brand: 'muted',
  product_compare_price: 'muted',
  product_tax_note: 'muted',
  product_sku: 'muted',
  product_short_description: 'muted',
  search_count: 'muted',
};

/**
 * What a theme says about the parts of one region, over and above its own CSS.
 *
 * `classes` is how a theme keeps a design it already has: Editorial's product
 * title is `text-4xl` and Tools & Machinery's is `text-3xl sm:text-[2.5rem]`,
 * and neither is a step on the merchant's type scale. A theme names the raw
 * classes for the parts it has an opinion about, they apply wherever the
 * merchant left that part's typography at "the theme's own", and the moment the
 * merchant chooses a size the theme's string stops applying - which is exactly
 * what "the theme's own" and "mine" should mean.
 */
export interface PartPresentation {
  /** Raw classes per part type, applied while the merchant has not overridden. */
  classes?: Partial<Record<PartType, string>>;
  /** Whether this theme's buttons run the width of their column. */
  wideButtons?: boolean;
  /**
   * How this theme photographs a product: Editorial stands it portrait, Bold
   * runs it wide and edge to edge, Serra puts the thumbnails in a rail. Applied
   * wherever the gallery part is left at "the theme's own", which is where every
   * shop starts.
   */
  gallery?: {
    aspect?: 'square' | 'portrait' | 'wide';
    thumbs?: 'below' | 'rail';
    rounded?: boolean;
  };
}

export interface PartStyle {
  className: string;
  style: CSSProperties;
}

/**
 * Whether a part still spaces itself.
 *
 * True until the merchant touches either spacing control, and what every part
 * component checks before applying the margin it has always carried. Both
 * controls, not one: a merchant who sets only the space below has still said
 * something about this part's spacing, and leaving the old top margin in place
 * under a control that now reads "Small" would be the editor lying about the
 * page.
 */
export function partOwnsSpacing(part: PagePart): boolean {
  return part.space_above === 'theme' && part.space_below === 'theme';
}

/** Whether the merchant has said anything about this part's typography. */
function styledText(part: PagePart): boolean {
  return part.text_size !== 'theme' || part.text_weight !== 'theme' || part.text_case !== 'theme';
}

/**
 * The wrapper's classes and colours for one part.
 *
 * Text classes go on the wrapper rather than on the text itself, so they
 * cascade to whatever the part draws - a price and the was-price beside it, a
 * row's name and its number - without every part component having to thread a
 * className into three places.
 */
export function partStyle(
  part: PagePart,
  presentation: PartPresentation | undefined,
  device: SectionDevice | undefined,
): PartStyle {
  const above = part.space_above;
  const below = part.space_below;
  const themeClasses = presentation?.classes?.[part.type] ?? BASE_CLASSES[part.type];
  // A part painted in a colour of the merchant's own is a panel, exactly as
  // one painted from the presets is: padded and rounded.
  const ownBackground = resolveColor(part.background_color);
  const panel = ownBackground !== undefined || (part.background !== 'theme' && part.background !== 'none');

  return {
    className: cn(
      above && above !== 'theme' && SPACE_ABOVE[above],
      below && below !== 'theme' && SPACE_BELOW[below],
      part.align !== 'theme' && ALIGN[part.align],
      // The theme's own string, until the merchant disagrees with it. Both at
      // once would be two font sizes on one element, decided by whichever
      // Tailwind emitted last - which is not a thing a merchant can predict.
      !styledText(part) && themeClasses,
      part.text_size !== 'theme' && TEXT_SIZE[part.text_size],
      part.text_weight !== 'theme' && TEXT_WEIGHT[part.text_weight],
      part.text_case !== 'theme' && TEXT_CASE[part.text_case],
      panel && 'p-4',
      // On the shopper's page the breakpoints answer. In the customiser the
      // device is already known and a media query would report the merchant's
      // monitor rather than the frame they are looking at, so the caller that
      // knows says so and this drops the classes.
      device ? undefined : part.hidden_on.map((each) => HIDDEN_ON[each]),
    ),
    style: {
      color: partColor(part.text_color || BASE_COLORS[part.type] || ''),
      backgroundColor:
        part.background === 'theme' || part.background === 'none'
          ? undefined
          : BACKGROUND[part.background],
      // Text on the primary colour has to flip, or a dark brand colour eats
      // the copy sitting on it.
      ...(part.background === 'primary' && !part.text_color
        ? { color: 'var(--storefront-background)' }
        : {}),
      borderRadius: panel ? 'var(--storefront-radius)' : undefined,
      // The merchant's own colour and exact padding, over the presets. Only
      // the background and padding: the part's text colour is `text_color`.
      ...areaStyle({
        background_color: part.background_color,
        padding_top_px: part.padding_top_px,
        padding_bottom_px: part.padding_bottom_px,
        padding_left_px: part.padding_left_px,
        padding_right_px: part.padding_right_px,
      }),
    },
  };
}

/** A merchant's colour: one of the shop's roles, a hex, or the theme's own. */
export function partColor(value: string): string | undefined {
  if (!value) return undefined;
  return COLOR_ROLES[value] ?? value;
}

/**
 * Whether a part is painted at all on the screen being drawn.
 *
 * Two answers in one, because the two callers want opposite things from a
 * hidden part: the shopper's storefront drops it, and the customiser dims it -
 * a merchant has to be able to see what they took down in order to put it back.
 */
export function partIsShown(
  part: PagePart,
  device: SectionDevice | undefined,
  includeHidden: boolean,
): boolean {
  if (includeHidden) return true;
  if (!part.visible) return false;
  return !(device && part.hidden_on.includes(device));
}

/** How wide a button-shaped part runs, with the theme's own habit as the default. */
export function partIsWide(part: PagePart, presentation: PartPresentation | undefined): boolean {
  if (part.width === 'full') return true;
  if (part.width === 'auto') return false;
  return presentation?.wideButtons ?? false;
}
